import { useCallback, useContext, useEffect, useState, useMemo, useRef } from "react";

import { Container, Grid, Icon, Tooltip } from "@material-ui/core";
import {
  CancelOutlined,
  EditOutlined,
  DeleteOutlineOutlined,
  Check,
  Report,
} from "@material-ui/icons";

import Fieldset from "../../../../components/basics/Fieldset";
import TextFieldErp from "../../../../components/basics/TextFieldErp";
import Btn from "../../../../components/buttons/Btn";

import { GerenciamentoContratoContext } from "../contexts/GerenciamentoContratoContext";
import { ConfigurationContext } from "../contexts/ConfigurationContext";
import SpeedDialErp from "../../../../components/speeddial/SpeedDialErp";
import { ChipTitleStyled, DivBtn } from "../styles/GerenciamentoContrato";
import { Margin } from "../../../../theme/styles/Utils";
import { ModalAdicionarServico } from "./ModalAdicionarServico";
import TableDefault from "../../../../components/grid/TableDefault";
import useGerenciamentoContratoController from "../controllers/GerenciamentoContratoController";
import ModalCancelamentoServico from "./ModalCancelamentoServico";
import moment from "moment";
import MaskService from "../../../../utils/mask/maskService";
import {
  calcValorRetencaoLinha,
  sumRetencoesPercentuais,
} from "../utils/calcRetencoesNfse";
import { getPerfilTributarioDados } from "../../../../repositories/qerpapi/impostos/ImpostosRepository";

const AccordionInfoServico = ({ location }) => {
  const {
    totalService,
    setTotalService,
    itemsTable,
    setItemsTable,
    modality,
    clientInfos,
  } = useContext(GerenciamentoContratoContext);
  const { configuration } = useContext(ConfigurationContext);
  const { handleTotalService, handleCancelServiceMain } =
    useGerenciamentoContratoController();
  const [modalServiceOpen, setModalServiceOpen] = useState(false);
  const [modalCancelOpen, setModalCancelOpen] = useState(false);
  // eslint-disable-next-line
  const [serviceId, setServiceId] = useState(null);
  const [serviceCancelId, setServiceCancelId] = useState(null);
  const [footerData, setFooterData] = useState(null);
  const perfisDetalheTentadosRef = useRef(new Set());

  const decimals = useMemo(
    () => configuration?.contract_decimal_size || 2,
    [configuration]
  );

  const exibirRetencoesLiquido = useMemo(
    () => configuration?.utiliza_nova_nfse === 1,
    [configuration]
  );

  const formatMaskedReal = useCallback(
    (numericValue) => {
      return MaskService.toMask(
        "decimal",
        numericValue.toFixed(decimals),
        decimals
      );
    },
    [decimals]
  );

  const formatMaskedRealTwoDecimals = useCallback((numericValue) => {
    return MaskService.toMask("decimal", numericValue.toFixed(2), 2);
  }, []);

  const getLinhaRetencaoELiquido = useCallback(
    (item) => {
      const bruto =
        MaskService.removeMaskDecimal(item?.valueTotal, decimals) || 0;
      const perfilLinha = item?.service?.perfil_tributario;

      const percentualLinha = (true || exibirRetencoesLiquido)
        ? sumRetencoesPercentuais(
            perfilLinha,
            clientInfos?.simples_nacional,
            bruto
          )
        : 0;

      if (percentualLinha <= 0) {
        return { retencao: 0, liquido: bruto };
      }

      const retencao = calcValorRetencaoLinha(bruto, percentualLinha);
      const liquido = Math.max(0, bruto - retencao);

      return { retencao, liquido };
    },
    [decimals, exibirRetencoesLiquido, clientInfos?.simples_nacional]
  );

  useEffect(() => {
    if (!exibirRetencoesLiquido || !itemsTable?.length) return;

    const linhasSemDetalhe = itemsTable.filter((item) => {
      const perfilId = item?.service?.perfil_tributario_id;
      if (!perfilId || item?.service?.perfil_tributario?.configuracaoNfse) {
        return false;
      }
      return !perfisDetalheTentadosRef.current.has(String(perfilId));
    });

    if (!linhasSemDetalhe.length) return;

    let cancelled = false;

    (async () => {
      const detalhesPorId = {};
      const ids = [
        ...new Set(
          linhasSemDetalhe.map((item) =>
            String(item.service.perfil_tributario_id)
          )
        ),
      ];

      ids.forEach((perfilId) =>
        perfisDetalheTentadosRef.current.add(perfilId)
      );

      await Promise.all(
        ids.map(async (perfilId) => {
          try {
            const detalhe = await getPerfilTributarioDados(perfilId);
            if (detalhe?.configuracaoNfse) {
              detalhesPorId[perfilId] = detalhe;
            }
          } catch (error) {
            console.error("Erro ao carregar perfil tributário da linha:", error);
          }
        })
      );

      if (cancelled || !Object.keys(detalhesPorId).length) return;

      setItemsTable((prev) => {
        if (!prev?.length) return prev;
        let changed = false;
        const next = prev.map((item) => {
          const perfilId = item?.service?.perfil_tributario_id;
          const detalhe = perfilId ? detalhesPorId[String(perfilId)] : null;
          if (!detalhe || item?.service?.perfil_tributario?.configuracaoNfse) {
            return item;
          }
          changed = true;
          return {
            ...item,
            service: {
              ...item.service,
              perfil_tributario: detalhe,
            },
          };
        });
        return changed ? next : prev;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [exibirRetencoesLiquido, itemsTable, setItemsTable]);

  useEffect(() => {
    handleTotalService();
  }, [handleTotalService, itemsTable]);

  const handleEditService = (id) => {
    setServiceId(id);
    setModalServiceOpen(true);
  };

  const handleCancelService = (id, keyService) => {
    setServiceCancelId(id);
    if (keyService) setModalCancelOpen(true);
    else handleCancelServiceMain(id, !!location?.state?.id);
  };

  useEffect(() => {
    if (!itemsTable || itemsTable.length === 0) {
      setFooterData(null);
      return;
    }

    let totalValor = 0;
    let totalRetencao = 0;
    let totalLiquido = 0;

    itemsTable.forEach((item) => {
      if (!item.rowError && !item.cancelDate) {
        const bruto = MaskService.removeMaskDecimal(item?.valueTotal, decimals);
        const { retencao, liquido } = getLinhaRetencaoELiquido(item);
        totalValor += bruto;
        totalRetencao += retencao;
        totalLiquido += liquido;
      }
    });

    const footerRow = {
      Qtde: "TOTAL",
      "Vlr. Total R$": `R$ ${formatMaskedRealTwoDecimals(totalValor)}`,
      "Data Cancelamento": "",
    };

    if (exibirRetencoesLiquido) {
      footerRow["(-) Retenções R$"] = `R$ ${formatMaskedReal(totalRetencao)}`;
      footerRow["Total Líquido R$"] = `R$ ${formatMaskedRealTwoDecimals(
        totalLiquido
      )}`;
    }

    setFooterData([footerRow]);
  }, [
    itemsTable,
    configuration,
    decimals,
    getLinhaRetencaoELiquido,
    formatMaskedReal,
    formatMaskedRealTwoDecimals,
    exibirRetencoesLiquido,
  ]);

  const columns = [
    {
      label: "Ações",
      format: (item) => (
        <SpeedDialErp
          actions={[
            {
              disabled: !!item.rowError,
              icon: <EditOutlined />,
              name: "Editar Item",
              action: () => handleEditService(item?.id),
            },
            {
              disabled: !!item.rowError,
              icon: item?.keyService ? (
                <CancelOutlined />
              ) : (
                <DeleteOutlineOutlined />
              ),
              name: `${item?.keyService ? "Cancelar" : "Deletar"} Item`,
              action: () => handleCancelService(item?.id, item?.keyService),
            },
          ]}
        />
      ),
      align: "center",
      disablePadding: true,
    },
    {
      label: "Código",
      align: "center",
      format: (item) => item?.service?.id,
      disablePadding: false,
    },
    {
      label: "Descrição",
      align: "center",
      format: (item) => item?.service?.descricao,
      disablePadding: true,
    },
    {
      label: "NBS",
      align: "center",
      format: (item) => item?.service?.nbs_codigo,
      disablePadding: true,
    },
    {
      label: "Vlr. Unitário",
      align: "center",
      format: (item) => `R$ ${item?.valueItem}`,
      disablePadding: true,
    },
    {
      label: "Qtde",
      align: "center",
      format: (item) => item?.amountItem,
      disablePadding: true,
    },
    {
      label: "Vlr. Total R$",
      align: "center",
      format: (item) =>
        `R$ ${formatMaskedRealTwoDecimals(
          MaskService.removeMaskDecimal(item?.valueTotal || "0", 2)
        )}`,
      disablePadding: true,
    },
    ...(exibirRetencoesLiquido
      ? [
          {
            label: "(-) Retenções R$",
            align: "center",
            format: (item) => {
              const { retencao } = getLinhaRetencaoELiquido(item);
              return `R$ ${formatMaskedReal(retencao)}`;
            },
            disablePadding: true,
          },
          {
            label: "Total Líquido R$",
            align: "center",
            format: (item) => {
              const { liquido } = getLinhaRetencaoELiquido(item);
              return `R$ ${formatMaskedRealTwoDecimals(liquido)}`;
            },
            disablePadding: true,
          },
          {
            label: "Perfil Tributário",
            align: "center",
            format: (item) =>
              item?.service?.perfil_tributario_id ? (
                <Check style={{ color: "green" }} />
              ) : (
                <Tooltip title="Perfil Tributário não selecionado">
                  <Report style={{ color: "red" }} />
                </Tooltip>
              ),
            disablePadding: true,
          },
        ]
      : []),
    {
      label: "Data Cancelamento",
      align: "center",
      format: (item) =>
        item?.cancelDate
          ? `${moment(item?.cancelDate).format("DD/MM/YYYY")}`
          : "",
      disablePadding: true,
    },
  ];

  return (
    <>
      {modalServiceOpen && (
        <ModalAdicionarServico
          open={modalServiceOpen}
          setOpen={setModalServiceOpen}
          locationId={serviceId}
        />
      )}
      {modalCancelOpen && (
        <ModalCancelamentoServico
          open={modalCancelOpen}
          setOpen={setModalCancelOpen}
          handleMain={() =>
            handleCancelServiceMain(serviceCancelId, !!location?.state?.id)
          }
        />
      )}
      <Margin top={20}>
        <Container maxWidth="xl">
          <Fieldset
            titulo={
              <Grid container spacing={4}>
                <Grid item xs>
                  <ChipTitleStyled
                    label="INFORMAÇÕES DOS SERVIÇOS PRESTADOS"
                    bgcolor="lightgrey"
                    textcolor="black"
                  />
                </Grid>
                <Grid item container xs={5} spacing={2}>
                  <Grid item xs>
                    <TextFieldErp
                      variant="filled"
                      label="Total"
                      textAlignRight
                      value={totalService}
                      setValue={setTotalService}
                      mask="decimal"
                      decimals={configuration?.contract_decimal_size || 2}
                      disabled
                    />
                  </Grid>
                  <DivBtn item xs>
                    <Btn
                      label="ADICIONAR SERVIÇO"
                      startIcon={<Icon>add</Icon>}
                      onFocus={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation();
                        setServiceId(null);
                        setModalServiceOpen(true);
                      }}
                      disabled={modality?.value ? false : true}
                      bgcolor="tertiary"
                      full="true"
                    />
                  </DivBtn>
                </Grid>
              </Grid>
            }
            bgcolor="white"
            textcolor="black"
            accordion
            defaultExpanded
          >
            <Grid item xs>
              <TableDefault
                selectable={false}
                tableTitle=""
                columns={columns}
                values={itemsTable}
                smallFont
                footerData={footerData}
              />
            </Grid>
          </Fieldset>
        </Container>
      </Margin>
    </>
  );
};

export default AccordionInfoServico;
